import React, { useState } from 'react';
import { Stack, Typography, Box, Button, Alert } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import { toast } from 'react-toastify';

import { DenseTable, DenseColumn } from '../../../../components/groups-teams/shared/DenseTable';
import { GT_COLORS } from '../../../../constants/designTokens.constants';
import { bulkActionsApi } from '../../../../services/bulk-actions/bulkActionsApi';
import { BulkUpdateCommitResponse, CommitFailure } from '../../../../types/bulkActions.types';

interface StatusConfig {
    severity: 'success' | 'warning' | 'error';
    getMessage: (r: BulkUpdateCommitResponse) => string;
}

const statusConfig: Record<string, StatusConfig> = {
    COMPLETED: {
        severity: 'success',
        getMessage: (r) => `All ${r.totalSuccessful} user${r.totalSuccessful !== 1 ? 's' : ''} successfully updated.`,
    },
    PARTIAL: {
        severity: 'warning',
        getMessage: (r) => `${r.totalSuccessful} of ${r.totalAttempted} user${r.totalAttempted !== 1 ? 's' : ''} updated. ${r.totalFailed} failed.`,
    },
    FAILED: {
        severity: 'error',
        getMessage: (r) =>
            r.totalSuccessful > 0
                ? `Only ${r.totalSuccessful} of ${r.totalAttempted} users updated. ${r.totalFailed} failed.`
                : 'Bulk update failed. No users were updated.',
    },
};

const failureColumns: DenseColumn<CommitFailure>[] = [
    {
        id: 'rowNumber',
        label: 'Row #',
        width: 70,
        sortable: true,
        getSortValue: (row) => row.rowNumber,
        render: (row) => <Typography fontSize={13}>{row.rowNumber}</Typography>,
    },
    {
        id: 'userId',
        label: 'User ID',
        width: 240,
        render: (row) => (
            <Typography fontSize={12} fontFamily="monospace" color={GT_COLORS.textMuted}>
                {row.userId}
            </Typography>
        ),
    },
    {
        id: 'error',
        label: 'Error',
        render: (row) => (
            <Typography fontSize={13} color={GT_COLORS.red}>
                {row.error}
            </Typography>
        ),
    },
];

interface ResultStepProps {
    commitResult: BulkUpdateCommitResponse;
    attemptId: string;
    onStartNew: () => void;
}

export const ResultStep: React.FC<ResultStepProps> = ({
    commitResult,
    attemptId,
    onStartNew,
}) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const config = statusConfig[commitResult.status] || statusConfig.FAILED;

    const handleDownloadReport = async () => {
        setIsDownloading(true);
        try {
            await bulkActionsApi.downloadReport(attemptId);
        } catch {
            toast.error('Failed to download report.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Stack spacing={3}>
            {/* Status banner */}
            <Alert severity={config.severity} sx={{ borderRadius: '8px', fontSize: 15 }}>
                {config.getMessage(commitResult)}
            </Alert>

            {/* Summary stats */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 140px', p: 2, borderRadius: '8px', backgroundColor: GT_COLORS.headerBg, textAlign: 'center' }}>
                    <Typography fontSize={22} fontWeight={700} color={GT_COLORS.textPrimary}>
                        {commitResult.totalAttempted}
                    </Typography>
                    <Typography fontSize={12} color={GT_COLORS.textMuted}>Total Attempted</Typography>
                </Box>
                <Box sx={{ flex: '1 1 140px', p: 2, borderRadius: '8px', backgroundColor: GT_COLORS.greenBg, textAlign: 'center' }}>
                    <Typography fontSize={22} fontWeight={700} color={GT_COLORS.green}>
                        {commitResult.totalSuccessful}
                    </Typography>
                    <Typography fontSize={12} color={GT_COLORS.textMuted}>Successful</Typography>
                </Box>
                <Box sx={{
                    flex: '1 1 140px', p: 2, borderRadius: '8px', textAlign: 'center',
                    backgroundColor: commitResult.totalFailed > 0 ? GT_COLORS.redBg : GT_COLORS.headerBg,
                }}>
                    <Typography fontSize={22} fontWeight={700} color={commitResult.totalFailed > 0 ? GT_COLORS.red : GT_COLORS.textSecondary}>
                        {commitResult.totalFailed}
                    </Typography>
                    <Typography fontSize={12} color={GT_COLORS.textMuted}>Failed</Typography>
                </Box>
            </Box>

            {/* New tags created info */}
            {commitResult.newTagsCreated.length > 0 && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                    <Typography fontSize={14}>
                        <strong>{commitResult.newTagsCreated.length} new tag{commitResult.newTagsCreated.length !== 1 ? 's' : ''}</strong> created: {commitResult.newTagsCreated.map(t => t.name).join(', ')}
                    </Typography>
                </Alert>
            )}

            {/* Failures table */}
            {commitResult.failures.length > 0 && (
                <Box sx={{ border: `1px solid ${GT_COLORS.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${GT_COLORS.borderLight}` }}>
                        <Typography fontSize={14} fontWeight={600} color={GT_COLORS.textPrimary}>
                            Failures ({commitResult.failures.length})
                        </Typography>
                    </Box>
                    <DenseTable
                        columns={failureColumns}
                        rows={commitResult.failures}
                        getRowKey={(row) => `failure-${row.rowNumber}`}
                        pageSize={100}
                        defaultSortColumn="rowNumber"
                        defaultSortDir="asc"
                    />
                </Box>
            )}

            {/* Action buttons */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button
                    variant="outlined"
                    startIcon={<ReplayIcon />}
                    onClick={onStartNew}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Start New Bulk Action
                </Button>

                <LoadingButton
                    variant="contained"
                    loading={isDownloading}
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadReport}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Download Report
                </LoadingButton>
            </Stack>
        </Stack>
    );
};
